/**
 * RoleGuard — restricts routes to specific user roles.
 * Mismatched roles are redirected to their role-specific home so
 * (e.g.) an admin who clicks a student-only link lands on /admin
 * rather than a 403 page.
 * Uses <Outlet /> pattern so it works as a layout route wrapper.
 */
import { Navigate, Outlet } from 'react-router-dom'
import { useAuth } from '@/stores/auth-context'
import type { UserRole } from '@/api/types'

interface RoleGuardProps {
  allowedRoles: UserRole[]
}

const ROLE_HOME: Record<UserRole, string> = {
  admin: '/admin',
  student: '/dashboard',
}

export default function RoleGuard({ allowedRoles }: RoleGuardProps) {
  const { user, isAuthenticated, isLoading } = useAuth()

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="h-6 w-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    )
  }

  if (!isAuthenticated || !user) {
    return <Navigate to="/login" replace />
  }

  if (!allowedRoles.includes(user.role)) {
    return <Navigate to={ROLE_HOME[user.role] ?? '/forbidden'} replace />
  }

  return <Outlet />
}
